<template>
    <div>
        <b-datatable
            :key="datatableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            has-filter-selector
            :initial-filters="initialFilters"
            item-key="id"
        >
            <template #buttons>
                <div class="px-2">
                    <v-tooltip top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                v-if="can('api.v1.pharmacy-sites.store') && can('pharmacy-sites.create')"
                                color="primary"
                                icon
                                v-bind="attrs"
                                :href="createItemUrl()"
                                v-on="on"
                            >
                                <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('pharmacy-sites::button.create') }}</span>
                    </v-tooltip>
                </div>
            </template>
            <template #filters="{ filters }">
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.site_name"
                        :label="t('pharmacy-sites::label.site_name')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.dms_id"
                        :label="t('pharmacy-sites::label.dms_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :readonly="!(can('api.v1.pharmacy-sites.store') && can('pharmacy-sites.create'))"
                        :disabled="!(can('api.v1.pharmacy-sites.store') && can('pharmacy-sites.create'))"
                        :items="filtersOptions.is_registered"
                        :label="t('consultants::label.registered')"
                        :clearable="filters.filters.is_registered !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>

            <template #[`item.site_name`]="{ item }">
                <div v-if="can('pharmacy-sites.get')" class="link" @click="$root.goTo(editItemUrl(item))">
                    {{ item.site_name }}
                </div>
                <div v-else>
                    {{ item.site_name }}
                </div>
            </template>

            <template #[`item.pharmacy_groups`]="{ item }">
                <span v-for="(value, key) in item.pharmacy_groups" :key="key">
                    {{ value.group_name }}
                </span>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('pharmacy-sites.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="can('api.v1.pharmacy-sites.update')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('pharmacy-sites::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="!can('api.v1.pharmacy-sites.update') && can('api.v1.pharmacy-sites.show')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(itemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('pharmacy-sites::button.details') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="can('api.v1.patients.store') && can('patients.create')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(patientsUrl(item))"
                            >
                                <v-icon>mdi-account-group</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('pharmacy-sites::button.patients') }}</span>
                    </v-tooltip>

                    <b-delete
                        v-if="can('api.v1.pharmacy-sites.destroy')"
                        :path="deleteItemUrl(item)"
                        :hide-true-btn="!!item.count_active_patients"
                        :text="
                            !!item.count_active_patients
                                ? t('pharmacy-sites::confirmation.have_active_users_content')
                                : t('default::confirmation.deletion')
                        "
                        :title="
                            !!item.count_active_patients
                                ? t('pharmacy-sites::confirmation.have_active_users_title')
                                : t('default::confirmation.general')
                        "
                        icon="mdi-trash-can"
                        @deleted="triggerDeleted"
                    >
                    </b-delete>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            initialFilters: {
                is_registered: 1,
            },
            hiddenColumns: ['id', 'surname'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('pharmacy-sites::label.site_name'),
                    value: 'site_name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('pharmacy-sites::label.registered'),
                    value: 'is_registered',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('pharmacy-sites::label.pharmacy_groups'),
                    value: 'pharmacy_groups',
                    sortable: false,
                    width: 180,
                },
                {
                    text: t('pharmacy-sites::label.chief_pharmacist_name'),
                    value: 'chief_pharmacist_name',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('pharmacy-sites::label.dms_id'),
                    value: 'dms_id',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'count_active_patients',
                    align: 'end',
                    width: 200,
                },
            ],
            filtersOptions: {
                is_registered: [
                    {
                        text: t('default::options.all'),
                        value: null,
                    },
                    {
                        text: t('default::options.yes'),
                        value: 1,
                    },
                    {
                        text: t('default::options.no'),
                        value: 0,
                    },
                ],
            },
        };
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('pharmacy-sites.export-record-as-pdf', { pharmacy_site: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        itemUrl(item) {
            return route('pharmacy-sites.get', { id: item.id });
        },
        editItemUrl(item) {
            return route('pharmacy-sites.get', { id: item.id });
        },
        createItemUrl() {
            return route('pharmacy-sites.create');
        },
        patientsUrl(item) {
            return route('patients.index', { pharmacy_site_id: item.id });
        },
        deleteItemUrl(item) {
            return route('api.v1.pharmacy-sites.destroy', { pharmacy_site: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify(
                'info',
                t('default::notification.success'),
                t('pharmacy-sites::notification.pharmacy_sites.deleted')
            );
        },
    },
};
</script>
